import React, { useState, useRef, useCallback } from 'react';
import Icon from './AppIcon';
import Button from './ui/Button';

const DirectoryUploadModal = ({ isOpen, onClose, onUpload }) => {
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [fileStructure, setFileStructure] = useState({});
  const [gameType, setGameType] = useState('webgpu');
  const directoryInputRef = useRef(null);

  // Build a tree structure from flat file list
  const buildFileTree = (fileList) => {
    const tree = {};
    
    fileList.forEach(file => {
      const parts = file.webkitRelativePath.split('/');
      let current = tree;
      
      parts.forEach((part, index) => {
        if (index === parts.length - 1) {
          // It's a file
          current[part] = {
            type: 'file',
            file: file,
            size: file.size,
            path: file.webkitRelativePath
          };
        } else {
          // It's a directory
          if (!current[part]) {
            current[part] = {
              type: 'directory',
              children: {}
            };
          }
          current = current[part].children;
        }
      });
    });
    
    return tree;
  };

  // Validate the uploaded directory structure
  const validateGameStructure = (fileList) => {
    const filePaths = Array.from(fileList).map(f => f.webkitRelativePath);
    const errors = [];
    
    if (gameType === 'webgpu') {
      const requiredFiles = [
        'index.html',
        'Build/*.loader.js',
        'Build/*.data.br',
        'Build/*.wasm.br',
        'Build/*.framework.js.br'
      ];
      
      requiredFiles.forEach(pattern => {
        if (pattern.includes('*')) {
          const regex = new RegExp(pattern.replace('*', '.*'));
          if (!filePaths.some(path => regex.test(path))) {
            errors.push(`Missing required file matching: ${pattern}`);
          }
        } else {
          if (!filePaths.some(path => path.endsWith(pattern))) {
            errors.push(`Missing required file: ${pattern}`);
          }
        }
      });
    } else if (gameType === 'react') {
      const requiredFiles = ['index.html'];
      const requiredDirs = ['assets'];
      
      // Check for required files
      requiredFiles.forEach(file => {
        if (!filePaths.some(path => path.endsWith(file))) {
          errors.push(`Missing required file: ${file}`);
        }
      });
      
      // Check for assets directory (typical for Vite builds)
      requiredDirs.forEach(dir => {
        if (!filePaths.some(path => path.includes(`${dir}/`))) {
          errors.push(`Missing required directory: ${dir}/`);
        }
      });
    } else if (gameType === 'html5') {
      const requiredFiles = ['index.html'];
      
      // Check for required files
      requiredFiles.forEach(file => {
        if (!filePaths.some(path => path.endsWith(file))) {
          errors.push(`Missing required file: ${file}`);
        }
      });
    }
    
    return errors;
  };

  const handleDirectorySelect = (event) => {
    const fileList = Array.from(event.target.files);
    if (fileList.length === 0) return;
    
    // Validate structure
    const errors = validateGameStructure(fileList);
    if (errors.length > 0) {
      alert('Invalid game structure:\n' + errors.join('\n'));
      return;
    }
    
    setFiles(fileList);
    setFileStructure(buildFileTree(fileList));
  };

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    
    const items = Array.from(event.dataTransfer.items);
    const filePromises = [];
    
    const traverseFileTree = (item, path = '') => {
      return new Promise((resolve) => {
        if (item.isFile) {
          item.file(file => {
            file.webkitRelativePath = path + file.name;
            resolve(file);
          });
        } else if (item.isDirectory) {
          const dirReader = item.createReader();
          const entriesPromises = [];
          
          const readEntries = () => {
            dirReader.readEntries(entries => {
              if (entries.length > 0) {
                entries.forEach(entry => {
                  entriesPromises.push(traverseFileTree(entry, path + item.name + '/'));
                });
                readEntries();
              } else {
                Promise.all(entriesPromises).then(files => {
                  resolve(files.flat());
                });
              }
            });
          };
          
          readEntries();
        }
      });
    };
    
    items.forEach(item => {
      const entry = item.webkitGetAsEntry();
      if (entry) {
        filePromises.push(traverseFileTree(entry));
      }
    });
    
    Promise.all(filePromises).then(results => {
      const allFiles = results.flat();
      
      // Validate structure
      const errors = validateGameStructure(allFiles);
      if (errors.length > 0) {
        alert('Invalid game structure:\n' + errors.join('\n'));
        return;
      }
      
      setFiles(allFiles);
      setFileStructure(buildFileTree(allFiles));
    });
  }, []);

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleUpload = async () => {
    if (files.length === 0 || !onUpload) return;
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      // Create file manifest
      const manifest = files.map(file => ({
        path: file.webkitRelativePath,
        size: file.size,
        type: file.type || 'application/octet-stream'
      }));
      
      // Prepare for upload
      const uploadData = {
        files: files,
        manifest: manifest,
        structureType: 'directory',
        gameType: gameType
      };
      
      // Simulate progress (actual implementation would track real progress)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 500);
      
      await onUpload(uploadData);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      setTimeout(() => {
        onClose();
        setFiles([]);
        setFileStructure({});
        setUploadProgress(0);
        setIsUploading(false);
      }, 1000);
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload game: ' + error.message);
      setIsUploading(false);
    }
  };

  // Render file tree recursively
  const renderFileTree = (tree, level = 0) => {
    return Object.entries(tree).map(([name, item]) => {
      const indent = level * 20;
      
      if (item.type === 'directory') {
        return (
          <div key={name}>
            <div 
              className="flex items-center space-x-2 py-1 text-sm"
              style={{ paddingLeft: `${indent}px` }}
            >
              <Icon name="Folder" size={16} className="text-primary" />
              <span className="text-foreground font-medium">{name}/</span>
            </div>
            {item.children && renderFileTree(item.children, level + 1)}
          </div>
        );
      } else {
        return (
          <div 
            key={name}
            className="flex items-center space-x-2 py-1 text-sm"
            style={{ paddingLeft: `${indent}px` }}
          >
            <Icon name="File" size={16} className="text-muted-foreground" />
            <span className="text-muted-foreground">{name}</span>
            <span className="text-xs text-muted-foreground">
              ({(item.size / 1024).toFixed(1)} KB)
            </span>
          </div>
        );
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-foreground">Upload Game Directory</h2>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="X" size={24} />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {files.length === 0 ? (
            <div>
              {/* Game Type Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-foreground mb-3">
                  Game Type
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="gameType"
                      value="webgpu"
                      checked={gameType === 'webgpu'}
                      onChange={(e) => setGameType(e.target.value)}
                      className="w-4 h-4 text-primary border-border focus:ring-primary"
                    />
                    <span className="text-sm text-foreground">WebGPU (Unity)</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="gameType"
                      value="react"
                      checked={gameType === 'react'}
                      onChange={(e) => setGameType(e.target.value)}
                      className="w-4 h-4 text-primary border-border focus:ring-primary"
                    />
                    <span className="text-sm text-foreground">React (HTML5)</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="gameType"
                      value="html5"
                      checked={gameType === 'html5'}
                      onChange={(e) => setGameType(e.target.value)}
                      className="w-4 h-4 text-primary border-border focus:ring-primary"
                    />
                    <span className="text-sm text-foreground">HTML5 (Construct 2)</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="gameType"
                      value="gamedistribution"
                      checked={gameType === 'gamedistribution'}
                      onChange={(e) => setGameType(e.target.value)}
                      className="w-4 h-4 text-primary border-border focus:ring-primary"
                    />
                    <span className="text-sm text-foreground">GameDistribution</span>
                  </label>
                </div>
              </div>
              <div
                className="border-2 border-dashed border-border rounded-lg p-12 text-center cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => directoryInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                <Icon name="FolderOpen" size={48} className="mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Upload Complete Game Directory
                </h3>
                <p className="text-muted-foreground mb-4">
                  {gameType === 'webgpu' 
                    ? 'Drag and drop your Unity WebGPU build folder here or click to browse'
                    : gameType === 'react'
                    ? 'Drag and drop your React game build folder here or click to browse'
                    : 'Drag and drop your HTML5 game folder here or click to browse'
                  }
                </p>
                <p className="text-sm text-muted-foreground">
                  {gameType === 'webgpu'
                    ? 'The folder should contain: index.html, Build/, TemplateData/, and other game files'
                    : gameType === 'react'
                    ? 'The folder should contain: index.html, assets/, and other build files'
                    : 'The folder should contain: index.html and your game assets (images, audio, scripts)'
                  }
                </p>
              </div>

              <input
                ref={directoryInputRef}
                type="file"
                webkitdirectory="true"
                directory="true"
                multiple
                onChange={handleDirectorySelect}
                className="hidden"
              />

              <div className="mt-6 bg-muted/50 rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Expected Structure:</h4>
                <div className="text-sm text-muted-foreground font-mono">
                  {gameType === 'webgpu' ? (
                    <>
                      <div>📁 YourGame/</div>
                      <div className="ml-4">📄 index.html</div>
                      <div className="ml-4">📄 manifest.webmanifest</div>
                      <div className="ml-4">📄 ServiceWorker.js</div>
                      <div className="ml-4">📁 Build/</div>
                      <div className="ml-8">📄 *.loader.js</div>
                      <div className="ml-8">📄 *.data.br</div>
                      <div className="ml-8">📄 *.wasm.br</div>
                      <div className="ml-8">📄 *.framework.js.br</div>
                      <div className="ml-4">📁 TemplateData/</div>
                      <div className="ml-8">📄 style.css</div>
                      <div className="ml-8">📄 favicon.ico</div>
                      <div className="ml-8">📄 ...</div>
                    </>
                  ) : gameType === 'react' ? (
                    <>
                      <div>📁 YourReactGame/</div>
                      <div className="ml-4">📄 index.html</div>
                      <div className="ml-4">📁 assets/</div>
                      <div className="ml-8">📄 *.js</div>
                      <div className="ml-8">📄 *.css</div>
                      <div className="ml-8">📄 *.png, *.jpg, *.svg</div>
                      <div className="ml-8">📄 ...</div>
                    </>
                  ) : (
                    <>
                      <div>📁 YourHTML5Game/</div>
                      <div className="ml-4">📄 index.html</div>
                      <div className="ml-4">📄 c2runtime.js (Construct 2)</div>
                      <div className="ml-4">📄 data.js</div>
                      <div className="ml-4">📄 jquery-*.min.js</div>
                      <div className="ml-4">📁 images/</div>
                      <div className="ml-8">📄 *.png, *.jpg</div>
                      <div className="ml-4">📁 media/</div>
                      <div className="ml-8">📄 *.m4a, *.ogg</div>
                      <div className="ml-4">📄 ...</div>
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div>
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Selected Files ({files.length} files)
                </h3>
                <div className="bg-muted/50 rounded-lg p-4 max-h-96 overflow-y-auto">
                  {renderFileTree(fileStructure)}
                </div>
              </div>

              {isUploading && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Uploading...</span>
                    <span className="text-sm font-mono text-foreground">{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-border flex justify-between">
          <Button
            variant="outline"
            onClick={() => {
              setFiles([]);
              setFileStructure({});
            }}
            disabled={files.length === 0 || isUploading}
          >
            Clear Selection
          </Button>
          <div className="space-x-3">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleUpload}
              disabled={files.length === 0 || isUploading}
              iconName={isUploading ? "Loader2" : "Upload"}
            >
              {isUploading ? 'Uploading...' : 'Upload Game'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DirectoryUploadModal;
